import { useEffect, useRef, useState } from 'react';

const MODAL_ROOT_CLASS = 'modal-root';

export interface UsePortalResult {
  portalEl: HTMLDivElement | null;
  /**
   * True once the portal element has been appended to the document. Use this
   * to gate anything that requires the portal to be attached (e.g. focus-trap
   * activation), so that DOM queries like document.querySelector work.
   */
  mounted: boolean;
}

export function usePortal(modalId: string): UsePortalResult {
  const modalRootRef = useRef<HTMLDivElement | null>(null);
  const [mounted, setMounted] = useState(false);

  // Create the portal element synchronously so it exists from the very first
  // render. This decouples its existence from the useEffect timing that
  // appends it to the document, preventing React 18's effect batching from
  // causing FocusTrap to mount and activate while the portal is still
  // detached. SSR-safe: returns null when document is unavailable.
  const [portalEl] = useState<HTMLDivElement | null>(() =>
    typeof document !== 'undefined' ? document.createElement('div') : null,
  );

  useEffect(() => {
    modalRootRef.current = getOrCreateModalRoot();
  }, []);

  useEffect(() => {
    if (portalEl) {
      portalEl.setAttribute('id', modalId);
      modalRootRef.current?.appendChild(portalEl);
      setMounted(true);
    }
    return () => {
      if (portalEl) {
        modalRootRef.current?.removeChild(portalEl);
        setMounted(false);
      }
    };
  }, [portalEl, modalId]);

  return { portalEl, mounted };
}

function getOrCreateModalRoot(): HTMLDivElement {
  const existing = document.querySelector<HTMLDivElement>(
    `.${MODAL_ROOT_CLASS}`,
  );
  if (existing) {
    return existing;
  }

  const root = document.createElement('div');
  root.classList.add(MODAL_ROOT_CLASS);
  document.body.appendChild(root);
  return root;
}
